import { useEffect, useState } from "react";
import { useInView, useReducedMotion } from "@/lib/motion";
import { DAY, DayMap, Label, Slab } from "./day";
import { LeafScene } from "./Phone";

/**
 * How it works, as four slab cards: the intake box, the first lesson on the map, a lesson beat, the map growing.
 * The page scrolls normally; each card's picture plays on its own when it comes into view.
 * Reduced motion: each picture at rest.
 */
const STEPS = [
  {
    title: "Tell it where you’re headed",
    body: "A topic, a question or a goal, in your own words. If it’s a step toward something bigger, it asks about the bigger thing, so you learn what you’ll actually use.",
  },
  {
    title: "Start where you are",
    body: "No 40-hour syllabus. You start with the one lesson you need first, and each one after it is shaped by how the last one went.",
  },
  {
    title: "Learn in lessons that stick",
    body: "One idea at a time: an animated scene and a single line to read. Tap Continue when it clicks.",
  },
  {
    title: "Watch it add up",
    body: "Every lesson you finish joins your map, so you can see how far you’ve come and choose where to go next.",
  },
];

function MiniPhone({ narration = "Every leaf on Earth is quietly running a factory, and its power source is the Sun." }: { narration?: string }) {
  return (
    <div className="relative aspect-[9/19] w-[150px] overflow-hidden rounded-[28px] border-[5px] border-[#0B0A1C] bg-[#05061A] shadow-[0_30px_80px_-20px_rgba(107,78,255,0.6),0_0_0_1px_rgba(255,255,255,0.12)] sm:w-[190px]">
      <LeafScene beat={0} />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent via-[rgba(5,6,26,0.9)] via-35% to-[#05061A] px-2.5 pt-8 pb-2.5">
        <p className="text-[9.5px] leading-snug font-extrabold text-white sm:text-[11px]">{narration}</p>
        <div className="mt-1.5 flex justify-between text-[7.5px] font-bold text-white/50 sm:text-[8.5px]">
          <span>I know this</span>
          <span>Ask a question</span>
        </div>
        <div className="mt-1 grid h-6 place-items-center rounded-lg border-b-2 border-[#4128B8] bg-[#6B4EFF] text-[8px] font-black tracking-[0.06em] text-white sm:h-7 sm:text-[9px]">CONTINUE</div>
      </div>
    </div>
  );
}

/** Step 1: the intake box, typing a goal. */
function AskPicture({ play }: { play: boolean }) {
  const text = "A computer made of mushrooms";
  const [n, setN] = useState(play ? 0 : text.length);
  useEffect(() => {
    if (!play) return;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= text.length) clearInterval(t);
    }, 45);
    return () => clearInterval(t);
  }, [play]);
  return (
    <div className="flex h-full flex-col justify-center gap-3 px-5">
      <p className="text-center text-lg font-black text-[#1C1A27]">What do you want to learn?</p>
      <div className="flex h-12 items-center rounded-2xl border-2 border-b-[5px] border-[#E7E3F0] bg-white px-4 text-[14px] font-extrabold text-[#1C1A27]">
        <span className="min-w-0 truncate">{text.slice(0, n)}</span>
        <span className="la-blink text-[#6B4EFF]">|</span>
      </div>
      <div className="grid h-11 place-items-center rounded-2xl border-b-[5px] border-[#4128B8] bg-[#6B4EFF] text-[13px] font-black tracking-[0.06em] text-white">START</div>
    </div>
  );
}

function StepPicture({ i, reduced }: { i: number; reduced: boolean }) {
  const [ref, seen] = useInView<HTMLDivElement>("-15% 0px");
  const [upTo, setUpTo] = useState(reduced ? 99 : 0);
  // the map steps draw in node by node once the card is in view
  useEffect(() => {
    if (reduced || !seen || (i !== 1 && i !== 3)) return;
    const last = i === 1 ? 5 : 7;
    let k = i === 1 ? 0 : 4;
    setUpTo(k);
    const t = setInterval(() => {
      k += 1;
      setUpTo(k);
      if (k >= last) clearInterval(t);
    }, 420);
    return () => clearInterval(t);
  }, [seen, reduced, i]);
  return (
    <div ref={ref} className={`relative h-[300px] overflow-hidden rounded-2xl ${i === 0 ? "bg-[#F7F5FF]" : i === 2 ? "grid place-items-center bg-[#F7F5FF]" : ""}`}>
      {i === 0 && <AskPicture play={seen && !reduced} />}
      {i === 1 && <DayMap upTo={upTo} className="!rounded-none" />}
      {i === 2 && <MiniPhone />}
      {i === 3 && (
        <div className="absolute inset-x-0 bottom-0">
          <DayMap upTo={upTo} className="!rounded-none" />
        </div>
      )}
    </div>
  );
}

/**
 * How it works: the steps as stops on the map. The dotted path from the horizon (Dawn) runs down through a node
 * per step, and the page scrolls normally (no pinned or scroll-driven sections).
 */
export function Journey() {
  const reduced = useReducedMotion();
  return (
    <section id="how" aria-label="How it works" className="relative scroll-mt-16 overflow-hidden px-4 pb-24 sm:px-6" style={{ background: DAY.ground }}>
      <div aria-hidden className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 border-l-[6px] border-dotted border-white/95" />
      <div aria-hidden className="absolute top-[30%] -left-24 h-56 w-72 rounded-[70px]" style={{ background: DAY.groundDeep }} />
      <div aria-hidden className="absolute top-[62%] -right-20 size-64 rounded-full" style={{ background: DAY.groundDeep }} />
      <div className="relative mx-auto max-w-xl">
        <Slab className="mx-auto mt-2 px-6 py-6 text-center">
          <Label>How it works</Label>
          <h2 className="mt-2 text-[1.75rem] leading-[1.12] font-black tracking-tight text-balance text-[#1C1A27] sm:text-[2.3rem]">From one sentence to a course that fits you.</h2>
        </Slab>
        <ol className="flex flex-col">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex flex-col items-center">
              <span className="mt-12 mb-5 grid size-14 place-items-center rounded-full text-xl font-black text-white" style={{ background: DAY.primary, boxShadow: `0 6px 0 ${DAY.primaryDeep}, 0 0 0 8px rgba(255,255,255,0.45)` }}>
                {i + 1}
              </span>
              <Slab className="w-full p-4 sm:p-5">
                <StepPicture i={i} reduced={reduced} />
                <div className="px-1 pt-4 pb-1">
                  <h3 className="text-xl font-black text-[#1C1A27] sm:text-2xl">{s.title}</h3>
                  <p className="mt-2 leading-relaxed text-[#6B6780]">{s.body}</p>
                </div>
              </Slab>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
